/**
 * Turning drawn points into a layer's own coordinates, and back.
 *
 * A path is drawn in millimetres on the page, because that is where the pointer
 * is. It is stored as proportions of the layer's own box, because that is what
 * a shape is - and it is what makes resizing, flipping and turning apply to a
 * drawing exactly as they do to a rectangle, with no code that knows the
 * difference.
 *
 * Pure arithmetic, kept out of the pen tool so that the actions which build a
 * layer do not have to import a canvas component to do it.
 */

import type { PathNode } from '../model/types';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The box a set of points sits in.
 *
 * Control points count. A curve that bulges past its anchors would otherwise
 * be clipped by its own layer, or - worse - stored as proportions greater than
 * one and quietly rescaled on the next resize.
 */
export function extent( nodes: PathNode[] ): Box | null {
  if ( nodes.length === 0 ) return null;

  const xs: number[] = [];
  const ys: number[] = [];

  for ( const node of nodes ) {
    xs.push( node.x, node.in?.x ?? node.x, node.out?.x ?? node.x );
    ys.push( node.y, node.in?.y ?? node.y, node.out?.y ?? node.y );
  }

  const x = Math.min( ...xs );
  const y = Math.min( ...ys );

  return {
    x,
    y,
    // A path drawn as a straight horizontal line has no height at all, and a
    // layer with no height cannot be selected, resized or dragged.
    w: Math.max( 0.1, Math.max( ...xs ) - x ),
    h: Math.max( 0.1, Math.max( ...ys ) - y ),
  };
}

/** One point, as a proportion of that box. */
export function toUnit( node: PathNode, box: Box ): PathNode {
  const place = ( point: { x: number; y: number } ) => ( {
    x: ( point.x - box.x ) / box.w,
    y: ( point.y - box.y ) / box.h,
  } );

  const out: PathNode = place( node );

  if ( node.in ) out.in = place( node.in );
  if ( node.out ) out.out = place( node.out );

  return out;
}

/** And back, for editing a stored path in the place it is drawn. */
export function toPage( node: PathNode, box: Box ): PathNode {
  const place = ( point: { x: number; y: number } ) => ( {
    x: box.x + point.x * box.w,
    y: box.y + point.y * box.h,
  } );

  const out: PathNode = place( node );

  if ( node.in ) out.in = place( node.in );
  if ( node.out ) out.out = place( node.out );

  return out;
}
